import { useEffect, useRef } from 'react';
import './pageShared.css';

export function useReveal() {
  const ref = useRef(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return undefined;

    const nodes = Array.from(root.querySelectorAll('[data-reveal]'));
    if (!nodes.length) return undefined;

    const reduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduced || typeof IntersectionObserver === 'undefined') {
      nodes.forEach((n) => n.classList.add('is-visible'));
      return undefined;
    }

    root.classList.add('page-ready');

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -60px 0px', threshold: 0 }
    );

    nodes.forEach((n) => observer.observe(n));
    return () => observer.disconnect();
  }, []);

  return ref;
}

export function PageHero({ eyebrow, title, leads = [], meta = null, visual = null, className = '' }) {
  const solo = !visual;
  const headerClass = ['page-hero', solo ? 'page-hero-solo' : '', className]
    .filter(Boolean)
    .join(' ');
  const gridClass = ['lp-container', 'page-hero-grid', solo ? 'page-hero-grid-solo' : '']
    .filter(Boolean)
    .join(' ');

  return (
    <header className={headerClass}>
      <div className={gridClass}>
        <div className="page-hero-text">
          <p className="lp-eyebrow page-rise page-d1">{eyebrow}</p>
          <h1 className="lp-title page-hero-title page-rise page-d2">{title}</h1>
          {leads.map((lead, i) => (
            <p className="lp-lead page-rise page-d3" key={i}>
              {lead}
            </p>
          ))}
          {meta && <div className="page-hero-meta page-rise page-d4">{meta}</div>}
        </div>
        {visual && (
          <div className="page-hero-visual page-rise page-d3" aria-hidden="true">
            {visual}
          </div>
        )}
      </div>
    </header>
  );
}

export function SectionHead({ eyebrow, title, body, reveal = true, className = '' }) {
  const classes = ['lp-section-head', className].filter(Boolean).join(' ');
  return (
    <div className={classes} {...(reveal ? { 'data-reveal': 'true' } : {})}>
      {eyebrow && <p className="lp-eyebrow">{eyebrow}</p>}
      {title && <h2 className="lp-h2">{title}</h2>}
      {body && <p className="lp-sec-lead">{body}</p>}
    </div>
  );
}

export function PageCta({ eyebrow, title, body, primary, secondary, className = '' }) {
  const classes = ['lp-section', 'page-cta', className].filter(Boolean).join(' ');
  return (
    <section className={classes}>
      <div className="lp-container">
        <div className="lp-section-head" data-reveal="true">
          <p className="lp-eyebrow">{eyebrow}</p>
          <h2 className="lp-h2">{title}</h2>
          <p className="lp-sec-lead">{body}</p>
        </div>
        <div className="lp-cta-row" data-reveal="true">
          {primary && (
            <a className="lp-btn-primary" href={primary.href}>
              {primary.label}
            </a>
          )}
          {secondary && (
            <a className="lp-btn-secondary" href={secondary.href}>
              {secondary.label}
            </a>
          )}
        </div>
      </div>
    </section>
  );
}
